import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface UserAuthFormProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  type: 'login' | 'register';
  onSubmit: (data: { email: string; password: string; name?: string; companyName?: string }) => Promise<void>;
  showCompanyField?: boolean;
  defaultEmail?: string;
}

export function UserAuthForm({ className, type, onSubmit, showCompanyField, defaultEmail, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>(defaultEmail || "");
  const [password, setPassword] = React.useState<string>("");
  const [name, setName] = React.useState<string>("");
  const [companyName, setCompanyName] = React.useState<string>("");
  const [formErrors, setFormErrors] = React.useState<{
    name?: string;
    email?: string;
    password?: string;
    companyName?: string;
  }>({});
  const { toast } = useToast();

  // Update email if defaultEmail changes
  React.useEffect(() => {
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, [defaultEmail]);

  async function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    setFormErrors({});

    // Validierung
    const errors: {
      name?: string;
      email?: string;
      password?: string;
      companyName?: string;
    } = {};

    if (type === 'register' && !name.trim()) {
      errors.name = "Name ist erforderlich";
    }

    if (type === 'register' && showCompanyField && !companyName.trim()) {
      errors.companyName = "Firmenname ist erforderlich";
    }

    if (!email.trim()) {
      errors.email = "E-Mail ist erforderlich";
    }

    if (!password.trim()) {
      errors.password = "Passwort ist erforderlich";
    } else if (password.length < 6) {
      errors.password = "Passwort muss mindestens 6 Zeichen lang sein";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      await onSubmit({ email, password, name, companyName: showCompanyField ? companyName : undefined });
    } catch (error) {
      toast({
        title: "Authentication Error",
        description: error instanceof Error ? error.message : "Failed to authenticate",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <form onSubmit={handleSubmit}>
        <div className="grid gap-4">
          {type === 'register' && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Max Mustermann"
                  type="text"
                  autoCapitalize="words"
                  autoComplete="name"
                  autoCorrect="off"
                  disabled={isLoading}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={formErrors.name ? "border-destructive" : ""}
                />
                {formErrors.name && (
                  <p className="text-sm font-medium text-destructive">{formErrors.name}</p>
                )}
              </div>

              {showCompanyField && (
                <div className="grid gap-2">
                  <Label htmlFor="companyName">Firmenname</Label>
                  <Input
                    id="companyName"
                    placeholder="Meine Firma GmbH"
                    type="text"
                    autoCapitalize="words"
                    autoComplete="organization"
                    autoCorrect="off"
                    disabled={isLoading}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className={formErrors.companyName ? "border-destructive" : ""}
                  />
                  {formErrors.companyName && (
                    <p className="text-sm font-medium text-destructive">{formErrors.companyName}</p>
                  )}
                </div>
              )}
            </>
          )}
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              placeholder="name@example.com"
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={isLoading}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={formErrors.email ? "border-destructive" : ""}
            />
            {formErrors.email && (
              <p className="text-sm font-medium text-destructive">{formErrors.email}</p>
            )}
          </div>
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Passwort</Label>
              {type === 'login' && (
                <a href="/forgot-password" className="text-sm text-primary hover:underline">
                  Passwort vergessen?
                </a>
              )}
            </div>
            <Input
              id="password"
              placeholder="••••••••"
              type="password"
              autoCapitalize="none"
              autoComplete={type === 'login' ? "current-password" : "new-password"}
              disabled={isLoading}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={formErrors.password ? "border-destructive" : ""}
            />
            {formErrors.password && (
              <p className="text-sm font-medium text-destructive">{formErrors.password}</p>
            )}
          </div>
          <Button disabled={isLoading} type="submit" className="w-full">
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {type === 'login' ? "Anmelden" : "Registrieren"}
          </Button>
        </div>
      </form>
    </div>
  );
}
